import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { PublicVehicleCard, DealerNav, DealerFooter } from "@/components/PublicVehicleCard";
import { NewArrivalsAlert } from "@/components/LeadCapture";

export default function NewArrivals() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get(`/public/dealers/${slug}/new-arrivals`).then(({ data }) => { setData(data); setState("ok"); }).catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0B0E]"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] flex flex-col items-center justify-center text-center px-6 text-white">
        <h1 className="font-heading text-3xl font-black">Dealership not found</h1>
        <Link to="/" className="mt-6"><Button className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">Back to Vinconex</Button></Link>
      </div>
    );
  }

  const { dealer, vehicles, days, fallback } = data;
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white grid-noise" style={brandStyle(dealer.accent_color)} data-testid="new-arrivals-page">
      <DealerNav dealer={dealer} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link to={`/d/${dealer.slug}`} data-testid="back-to-dealer" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-4 w-4" /> All inventory
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand-soft text-xs font-mono-tech uppercase tracking-widest mb-4">
          <Sparkles className="h-3.5 w-3.5" /> {fallback ? "Latest on the lot" : `Added in the last ${days} days`}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight" data-testid="new-arrivals-heading">New arrivals at {dealer.name}</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">
          {fallback
            ? `Nothing new landed in the last ${days} days, so here are the most recent vehicles on the lot.`
            : `${vehicles.length} ${vehicles.length === 1 ? "vehicle" : "vehicles"} just hit the lot. Scan, explore, and grab yours before it's gone.`}
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="new-arrivals-grid">
          {vehicles.map((v) => <PublicVehicleCard key={v.id} v={v} showAdded />)}
        </div>
        <div className="mt-10">
          <NewArrivalsAlert dealer={dealer} />
        </div>
      </main>
      <DealerFooter dealer={dealer} />
    </div>
  );
}
